import { ref, reactive } from 'vue'
import { lookupData } from '../Base.js'
import {
  PI, cos, sin, toRads, toDegs, arcsin, arccos, arctan, sqrt, log, ln, exp,
  clampAngleNegativePiToPi,
  positiveModulo,
} from '../Common/Math.js'
import { describeFunc } from '../Common/CalcContext.js';

export let Months = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};

// Returns 1-365
export function getDayOfYear(monthIndex, dayOfMonth) {
  if (monthIndex < 0 || monthIndex > 11) {
    throw new Error(`Invalid monthIndex: ${monthIndex}`);
  }
  if (dayOfMonth < 1 || dayOfMonth > 31) {
    throw new Error(`Invalid dayOfMonth: ${dayOfMonth}`);
  }
  const date = new Date(2024, monthIndex, dayOfMonth);
  const startOfYear = new Date(2024, 0, 1);
  const dayOfYear = Math.floor((date - startOfYear) / (1000 * 60 * 60 * 24)) + 1;
  return dayOfYear;
}

// The surface azimuth angles of variable surface directions, in degrees
export let SurfaceAzimuthAngleMapDegrees = {
  S: 0,
  SSW: 22.5,
  SW: 45,
  WSW: 67.5,
  W: 90,
  WNW: 112.5,
  NW: 135,
  NNW: 157.5,
  N: 180,
  NNE: -157.5,
  NE: -135,
  ENE: -112.5,
  E: -90,
  ESE: -67.5,
  SE: -45,
  SSE: -22.5,
};

function getSurfaceAzimuthAngleDegrees(surfaceDir) {
  // If number - use as-is
  // If string - lookup in map
  if (typeof surfaceDir === 'number') {
    return surfaceDir;
  } else {
    return lookupData(SurfaceAzimuthAngleMapDegrees, [surfaceDir]);
  }
}


export function getPrimarySurfacesMap() {
  return {
    northWall: {
      label: 'North Wall',
      surfaceDir: 'N',
      tiltAngleDegrees: 90,
    },
    eastWall: {
      label: 'East Wall',
      surfaceDir: 'E',
      tiltAngleDegrees: 90,
    },
    southWall: {
      label: 'South Wall',
      surfaceDir: 'S',
      tiltAngleDegrees: 90,
    },
    westWall: {
      label: 'West Wall',
      surfaceDir: 'W',
      tiltAngleDegrees: 90,
    },
    roof: {
      label: 'Roof',
      surfaceDir: 'N',
      tiltAngleDegrees: 0,
    },
  }
}

// Solar azimuth
export function calcPhi(ctx, surfaceDir) {
  let azimuthAngle = getSurfaceAzimuthAngleDegrees(surfaceDir);
  return ctx.eval('abs(azimuthAngle/180.0 - 1)', {azimuthAngle}, 'phi');
}

export function calc_equation_of_time(ctx, n) {
  // EOT: the equation of time, in minutes
  // - the difference between the mean solar time and the apparent solar time (due to
  //   the eccentricity of the earth's orbit)
  // - this is added to the "Local Standard Time" to get the "Apparent Solar Time" (AST)
  // - note that the equation is essentially the same from year to year and for all longitudes
  // - https://aa.usno.navy.mil/faq/eqtime
  return ctx.eval('2.2918*(0.0075 + 0.1868*cos(gamma) - 3.2077*sin(gamma) - 1.4615*cos(2*gamma) - 4.089*sin(2*gamma))', {
    gamma: 2*PI*(n - 1)/365.0
  }, 'ET');
}

export function calc_apparent_solar_time(ctx, timeZone, localStdTime, longitude, n) {
  // AST: the apparent solar time
  // - the "actual" solar time (as observed by a sun dial) at a location, which may differ
  //   from the "local standard time" (aka clock time) for a person living in that time zone.
  // - this is calculated by adding the equation of time (and longitude correction) to the local standard time
  //
  // - timeZone is the UTC time zone value of the local (-12.0 to 14.0)
  //   - timeZone is -12 to 14, where 0 is Greenwich/UTC-0. Every +1 is 15 degrees E of Greenwich
  // - localStdTime is the local standard time (in hours) for the time zone (0.0-24.0)
  // - longitude is the longitude of the location (in degrees E of Greenwich, -180 to 180degs)
  // - returns the AST in hours (0.0-24.0) [note: wraps to this range]

  // We do ET/60 to convert to hours.
  // THe LSM is the local standard meridian, which is the longitude of the center of the time zone.
  // We do 4*(Lon-LSM)/60.0 b/c 4 minutes per degree of longitude difference.
  ctx.assert(-12 <= timeZone && timeZone <= 14, 'Invalid UTC time zone: ' + timeZone);
  ctx.assert(0 <= localStdTime && localStdTime <= 24, 'Invalid local standard time: ' + localStdTime);
  ctx.assert(-180 <= longitude && longitude <= 180, 'Invalid longitude: ' + longitude);
  ctx.ET = calc_equation_of_time(ctx, n);
  ctx.AST = ctx.eval('LST + ET/60.0 + 4*(Lon - LSM)/60.0', {
    LST: localStdTime,
    Lon: longitude,
    LSM: 15 * timeZone,
  }, 'AST');
  ctx.AST = ctx.eval('wrap(AST, 24)', {
    wrap: positiveModulo,
  }, 'AST');
  return ctx.AST;
}

export function calc_solar_declination_angle_rads(ctx, n) {
  // delta: the solar declination angle, in rads
  // - the angle between the rays of the sun and the plane of the earth's equator
  //   It is positive when then equator is tilted towards the sun (summer solstice) [northern hemisphere in sun],
  //   and negative when the equator is tilted away from the sun (winter solstice) [southern hemisphere in sun].
  //   In other words, the declination angle is the latitude of the subsolar point (the point on the earth's surface
  //   where the sun is directly overhead at noon).
  // - this varies throughout the year, and is 0 at the equinoxes (March 21 and September 23)
  // - it is +23.45 degrees at the summer solstice (June 21) and -23.45 degrees at the winter solstice (December 21)
  // - Note: it is the degress
  return ctx.eval('toRads(23.45*sin(2*PI*(n + 284)/365.0))', {
    toRads: toRads,
    n: n,
  }, 'declination_rads');
}

/*
export function calc_solar_altitude_angle_rads(ctx, declination_rads, AST, latitude) {
  // Returns:
  // - solar_altitude_rads: the solar altitude angle, in rads
  // 
  // Inputs:
  // - declination_rads, the solar declination angle, in rads
  // - AST, the apparent solar time, in hours (0.0-24.0)
  // - latitude, the latitude of the location, in degrees N (-90.0 to 90.0)
}
*/

export function calc_solar_position(ctx, declination_rads, AST, latitude) {
  // Returns:
  // - solar_altitude_rads: the solar altitude angle, in rads
  // - solar_azimuth_rads: the solar azimuth angle, in rads
  // 
  // Inputs:
  // - declination_rads, the solar declination angle, in rads
  // - AST, the apparent solar time, in hours (0.0-24.0)
  // - latitude, the latitude of the location, in degrees N (-90.0 to 90.0)
  //
  // References:
  // - ASHRAE Ch. 14.2
  // - https://www.omnicalculator.com/physics/sun-angle#the-formulas-for-solar-elevation-and-azimuth-angles-computing-the-suns-location 
  // - Nice calculator, with polar plots: https://www.pveducation.org/pvcdrom/properties-of-sunlight/sun-position-calculator
  //
  ctx.declination_rads = declination_rads;
  ctx.Lat_rads = toRads(latitude);

  // Calculate H, the hour angle (the angular displacement of the sun east/west of the location)
  // AST=12 means sun is directly overhead (zenith), AST > 12 means sun is west of zenith,
  // AST < 12 means sun is east of zenith.
  ctx.H_rads = ctx.eval('toRads(15*(AST - 12))', {
    AST: AST,
    toRads,
  }, 'H');

  // Calculate solar altitude angle (rads up from horizontal plane to the Earth-sun line)
  // On the equator, this value is 90degs at noon, and 0degs at sunrise/sunset. It is 
  // is <0degs when the sun is not visible (during night).
  // Total range is -90 to 90degs.
  ctx.solar_altitude_rads = ctx.eval('arcsin(cos(Lat_rads)*cos(declination_rads)*cos(H_rads) + sin(Lat_rads)*sin(declination_rads))', {
    arcsin,
  }, 'solar_altitude_rads');

  // Calculate solar azimuth angle (rads from line to south pole to Earth-sun line).
  // By convention, +ve for afternoon hours (west) and -ve for morning hours (east).
  // So: 90degs is due west, 0degs is due south, -90degs is due east, 180degs/-180degs is due north.
  // Note: the ASHRAE handbook does not specify exactly to get the angle from the sin and
  // cos, but the formulaes below should cover it.
  //ctx.sin_solar_azimuth_rads = ctx.eval('sin(H_rads)*sin(declination_rads)/cos(solar_altitude_rads)', {
  //}, 'sin_solar_azimuth_rads');
  // Handle zenith case
  // Check if cos(solar_altitude_rads) is within margin of 0
  if (Math.abs(cos(ctx.solar_altitude_rads)) < 1e-6) {
    ctx.solar_azimuth_rads = 0;
    return {
      solar_altitude_rads: ctx.solar_altitude_rads,
      solar_azimuth_rads: ctx.solar_azimuth_rads,
    }
  }
  ctx.cos_solar_azimuth_rads = ctx.eval('(cos(H_rads)*cos(declination_rads)*sin(Lat_rads) - sin(declination_rads)*cos(Lat_rads))/cos(solar_altitude_rads)', {
  }, 'cos_solar_azimuth_rads');
  if (ctx.H_rads >= 0) {
    // If H_rads >= 0, means sun is west of zenith (afternoon)
    // We should return a positive azimuth angle (0 to 180degs)
    ctx.solar_azimuth_rads = ctx.eval('arccos(cos_solar_azimuth_rads)', {
      arccos,
    }, 'solar_azimuth_rads');
  } else {
    // If H_rads < 0, means sun is east of zenith (morning)
    // We should return a negative azimuth angle (0 to -180degs)
    ctx.solar_azimuth_rads = ctx.eval('-arccos(cos_solar_azimuth_rads)', {
      arccos,
    }, 'solar_azimuth_rads');
  }

  return {
    solar_altitude_rads: ctx.solar_altitude_rads,
    solar_azimuth_rads: ctx.solar_azimuth_rads,
  }
}

export function calc_E_o(ctx, n) {
  // E_sc: the solar constant, in btu/h-ft^2
  // - the radiant flux on a surface (just outside earth's atmosphere) normal to the sun's rays at the mean distance
  // from the sun to the earth
  // - the value of E_sc is 433.3 btu/h-ft^2
  // E_o: the extraterrestrial radiant flux on a surface normal to the sun's rays
  // at the mean distance from the sun to the earth. It varies slightly througout the 
  // year due to the sun's changing distance from the earth.
  // Max is 447.6 at beginning of january and min is 419.2 at beginning of July
  return ctx.eval('E_sc*(1 + 0.033*cos(2*PI*(n - 3)/365.0))', {
    n: n,
    E_sc: 433.3,
  }, 'E_o');
}

export function calc_clear_sky_irradiances(ctx, solar_altitude_rads, tau_b, tau_d, E_o) {
  //! Calculate the clear sky solar radiation values (in Bthu/h-ft^2)
  //! E_b is the direct beam irradiance (on a surface normal to the sun's rays)
  //! - Due to light directly emanating from the solar disk
  //! E_d is the diffuse irradiance (on a horizontal surface)
  //! - Due to radiation emanating from the rest of the sky
  //!
  //! tau_b and tau_d are the beam and diffuse optical depths, respectively. They are an adjustment
  //! factor based on the location data for a given location (accounts for atmospheric conditions).
  //! ASHRAE weather data gives average values for the 21st day of each month.

  ctx.solar_altitude_rads = solar_altitude_rads;
  ctx.tau_b = tau_b;
  ctx.tau_d = tau_d;
  ctx.E_o = E_o;

  // If the sun is not visible, we have neglible solar radiation
  if (ctx.solar_altitude_rads < 0) {
    ctx.log("Sun not visible, setting E_b and E_d to 0");
    ctx.E_b = 0;
    ctx.E_d = 0;
    return {
      E_b: ctx.E_b,
      E_d: ctx.E_d,
      E_o: ctx.E_o,
    }
  }

  // The relative air mass (m) is the ratio of the mass in the actual earth/sun path to the mass
  // in the path if the sun were directly overhead.
  ctx.m = ctx.eval('1.0/(sin(solar_altitude_rads) + 0.50572*((6.07995 + solar_altitude_degs)^(-1.6364)))', {
    solar_altitude_degs: toDegs(ctx.solar_altitude_rads),
  }, 'm');
  ctx.ab = ctx.eval('1.454 - 0.406*tau_b - 0.268*tau_d + 0.021*tau_b*tau_d', {}, 'ab');
  ctx.ad = ctx.eval('0.507 + 0.205*tau_b - 0.080*tau_d - 0.190*tau_b*tau_d', {}, 'ad');
  ctx.E_b = ctx.eval('E_o*exp(-tau_b*(m^ab))', {
  }, 'E_b');
  ctx.E_d = ctx.eval('E_o*exp(-tau_d*(m^ad))', {
  }, 'E_d');
  return {
    E_b: ctx.E_b,
    E_d: ctx.E_d,
    E_o: ctx.E_o,
    debug: {
      m: ctx.m,
      ab: ctx.ab,
      ad: ctx.ad,
    }
  }
}

export function calc_radiation_on_surface(ctx,
  solar_altitude_rads,
  solar_azimuth_rads,
  surfaceDir, surfaceTiltAngleDegs, groundReflectance,
  irradianceValues,
) {
  //! Returns the solar irradiance on a tilted surface, broken down by components.
  //!
  //! surfaceTiltAngleDegs is the angle from horizontal to the surface
  //! - 0degs is horizontal, 90degs is vertical, >90degs is facing downwards

  ctx.solar_altitude_rads = solar_altitude_rads;
  ctx.solar_azimuth_rads = solar_azimuth_rads;
  ctx.tilt_angle_rads = toRads(surfaceTiltAngleDegs);
  ctx.surfaceTiltAngleDegs = surfaceTiltAngleDegs;
  ctx.E_b = irradianceValues.E_b;
  ctx.E_d = irradianceValues.E_d;
  ctx.E_o = irradianceValues.E_o;

  if (solar_altitude_rads <= 0) {
    // The sun is not visible, so we have negligible solar radiation
    ctx.log("Sun not visible, setting E_t_b, E_t_d, E_t_r to 0");
    return {
      E_t_b: 0,
      E_t_d: 0,
      E_t_r: 0,
      E_t: 0,
      surface_solar_incidence_degs: 0,
      surface_azimuth_diff_rads: 0,
    }
  }

  // Calc surface azimuth angle difference
  ctx.surface_azimuth_degs = getSurfaceAzimuthAngleDegrees(surfaceDir);
  ctx.surface_azimuth_rads = toRads(ctx.surface_azimuth_degs);
  // Note: if the diff is >90degs or <-90degs, it means the surface is shaded / facing
  // away from the sun.
  // +ve means that sun is to the right/clockwise of us. -ve means that sun is to the left/counter-clockwise of us.
  ctx.surface_azimuth_diff_rads = ctx.eval('clampAngleNegativePiToPi(solar_azimuth_rads - surface_azimuth_rads)', {
    clampAngleNegativePiToPi: clampAngleNegativePiToPi,
  }, 'surface_azimuth_diff_rads');

  ctx.cos_angle_of_incidence = ctx.eval('cos(solar_altitude_rads)*cos(surface_azimuth_diff_rads)*sin(tilt_angle_rads) + sin(solar_altitude_rads)*cos(tilt_angle_rads)', {
  }, 'cos_angle_of_incidence');

  // Math.acos returns a value in the range [0, pi]
  // We are in the sun if cos_angle_of_incidence_rads > 0 (surface_solar_incidence_degs < 90), and
  // we are in the shade if cos_angle_of_incidence_rads < 0 (surface_solar_incidence_degs > 90).
  ctx.surface_solar_incidence_degs = toDegs(Math.acos(ctx.cos_angle_of_incidence))

  // Direct beam solar irradiance
  if (ctx.cos_angle_of_incidence > 0) {
    // Surface is in sun
    ctx.E_t_b = ctx.eval('E_b*cos_angle_of_incidence', {}, 'E_t_b');
  } else {
    // Surface is shaded, so we have no direct beam solar irradiance
    ctx.E_t_b = 0;
  }

  // Diffuse solar irradiance (ASHRAE method)
  // Ansiotropy index AI
  ctx.AI = ctx.eval('E_b/E_o', {
  }, 'AI');
  // R_b should be zero when sun is not visible (altitude < 0) [already handled above], or 
  // when cos_angle_of_incidence <= 0 (surface is shaded)
  if (ctx.cos_angle_of_incidence > 0) {
    ctx.R_b = ctx.eval('cos_angle_of_incidence/sin(solar_altitude_rads)', {
    }, 'R_b');
  } else {
    ctx.log("Surface is shaded [cos(incidence) <= 0], setting R_b to 0");
    ctx.R_b = 0;
  }
  // Note: we clamp the tilt_angle to be between 0 and pi/2, since the ASHRAE method
  // is only valid for these angles. Should not really come up in practice.
  ctx.E_t_d = ctx.eval('E_d*(AI*R_b + (1 - AI)*(1 + cos(clamped_tilt_angle_rads))/2.0)', {
    clamped_tilt_angle_rads: Math.min(ctx.tilt_angle_rads, PI/2.0),
  }, 'E_t_d');

  // Reflected solar irradiance
  // Ground reflectance is the ground albedo value (usually about 0.2)
  ctx.rho_g = groundReflectance;
  // Note: we clamp the tilt_angle to be between 0 and pi/2, since the ASHRAE method
  // is only valid for these angles. Should not really come up in practice.
  ctx.E_t_r = ctx.eval('(E_b*sin(solar_altitude_rads) + E_d)*rho_g*(1 - cos(clamped_tilt_angle_rads))/2.0', {
    clamped_tilt_angle_rads: Math.min(ctx.tilt_angle_rads, PI/2.0),
  }, 'E_t_r');
  // Total
  ctx.E_t = ctx.eval('E_t_b + E_t_d + E_t_r', {}, 'E_t');

  return {
    E_t_b: ctx.E_t_b,
    E_t_d: ctx.E_t_d,
    E_t_r: ctx.E_t_r,
    E_t: ctx.E_t,
    surface_solar_incidence_degs: ctx.surface_solar_incidence_degs,
    surface_azimuth_diff_rads: ctx.surface_azimuth_diff_rads,
    debug: {
      surface_azimuth_degs: ctx.surface_azimuth_degs,
      cos_angle_of_incidence: ctx.cos_angle_of_incidence,
    }
  }
}

export class SolarRadiationValues {
  constructor(data) {
    this.E_t = data.E_t;
    this.E_t_b = data.E_t_b;
    this.E_t_d = data.E_t_d;
    this.E_t_r = data.E_t_r;
    this.solar_altitude_rads = data.solar_altitude_rads;
    this.surface_solar_incidence_degs = data.surface_solar_incidence_degs;
    this.surface_azimuth_diff_rads = data.surface_azimuth_diff_rads;
    this.debug = data.debug;
  }

  isSunVisibleInSky() {
    return this.solar_altitude_rads > 0;
  }

  isSurfaceInSun() {
    return this.surface_solar_incidence_degs < 90;
  }

  isSunToRight() {
    // True if the run is to the "right"/clockwise/east of the surface
    return this.surface_azimuth_diff_rads > 0;
  }

  isSunToLeft() {
    // True if the run is to the "left"/counter-clockwise/west of the surface
    return !this.isSunToRight();
  }
}

export let calc_clear_sky_solar_radiation = describeFunc('calc_clear_sky_solar_radition', {
  timeZone: 'None', // UTC time zone value
  localStdTime: 'None', // 0.00-23.99
  dayOfYear: 'None', // 1-365
  latitude: 'None',
  longitude: 'None',
  tau_b: 'None', // beam optical depth (from weather data)
  tau_d: 'None', // diffuse optical depth (from weather data)
}, 'None', (ctx, timeZone, localStdTime, dayOfYear, latitude, longitude, tau_b, tau_d) => {
  ctx.n = dayOfYear;

  // Calc AST
  ctx.AST = calc_apparent_solar_time(ctx, timeZone, localStdTime, longitude, ctx.n);
  // Note: we do not account for daylight savings time (DST) here, which is probably fine.

  // Solar declination angle
  ctx.declination_rads = calc_solar_declination_angle_rads(ctx, ctx.n);
  // Solar position
  let solarPos = calc_solar_position(ctx, ctx.declination_rads, ctx.AST, latitude);
  // Solar altitude
  ctx.solar_altitude_rads = solarPos.solar_altitude_rads;
  // Solar azimuth
  ctx.solar_azimuth_rads = solarPos.solar_azimuth_rads;

  // Calc the clear sky radiation values
  ctx.E_o = calc_E_o(ctx, ctx.n);
  let clearSkyValues = calc_clear_sky_irradiances(ctx, ctx.solar_altitude_rads,
    tau_b, tau_d, ctx.E_o);
  ctx.E_b = clearSkyValues.E_b;
  ctx.E_d = clearSkyValues.E_d;

  let res = {
    AST: ctx.AST,
    declination_rads: ctx.declination_rads,
    solar_altitude_rads: ctx.solar_altitude_rads,
    solar_azimuth_rads: ctx.solar_azimuth_rads,
    E_o: ctx.E_o,
    E_b: ctx.E_b,
    E_d: ctx.E_d,
  }
  return res;
}, {
  autoCache: true
});

export let apply_to_surface = describeFunc('apply_to_surface', {
  clearSkyValues: 'None', // the result of a call to clear_sky_values
  surfaceDir: 'None', // surface direction
  surfaceTiltAngleDegs: 'None', // surface tilt angle, in degrees
  groundReflectance: 'None', // ground reflectance
}, 'None', (ctx, clearSkyValues, surfaceDir, surfaceTiltAngleDegs, groundReflectance) => {
  // Apply the clear sky values to our particular surface
  let irradiances = {
    E_b: clearSkyValues.E_b,
    E_d: clearSkyValues.E_d,
    E_o: clearSkyValues.E_o,
  }
  let surfaceValues = calc_radiation_on_surface(
    ctx, clearSkyValues.solar_altitude_rads, clearSkyValues.solar_azimuth_rads,
    surfaceDir, surfaceTiltAngleDegs, groundReflectance, irradiances);
  ctx.E_t_b = surfaceValues.E_t_b;
  ctx.E_t_d = surfaceValues.E_t_d;
  ctx.E_t_r = surfaceValues.E_t_r;
  ctx.E_t = surfaceValues.E_t;
  ctx.surface_solar_incidence_degs = surfaceValues.surface_solar_incidence_degs;
  ctx.surface_azimuth_diff_rads = surfaceValues.surface_azimuth_diff_rads;

  return {
    E_t_b: ctx.E_t_b,
    E_t_d: ctx.E_t_d,
    E_t_r: ctx.E_t_r,
    E_t: ctx.E_t,
    surface_solar_incidence_degs: ctx.surface_solar_incidence_degs,
    surface_azimuth_diff_rads: ctx.surface_azimuth_diff_rads,
    debug: {
      ...surfaceValues.debug,
    }
  }
}, {
  autoCache: true
});

/**
 * Calculate solar irradiance on a tilted surface.
 * In general use this func instead of the functions above (more compact).
 * This function performs a bunch of related solar calculations internally.
 * 
 * See Ch. 14.2 of ASHRAE Fundamentals Handbook (2021) for details.
 */
export let calc_solar_radiation = describeFunc('calc_solar_radiation', {
  timeZone: 'None', // UTC time zone value
  localStdTime: 'None', // 0.00-23.99
  dayOfYear: 'None', // 1-365
  latitude: 'None',
  longitude: 'None',
  tau_b: 'None', // beam optical depth (from weather data)
  tau_d: 'None', // diffuse optical depth (from weather data)
  surfaceDir: 'None', // surface direction
  surfaceTiltAngleDegs: 'None', // surface tilt angle, in degrees
  groundReflectance: 'None', // ground reflectance
}, 'None', (ctx, timeZone, localStdTime, dayOfYear, latitude, longitude,
  tau_b, tau_d, surfaceDir, surfaceTiltAngleDegs,
  groundReflectance) => {

  // Calc clear sky values
  let clearSkyValues = ctx.call(calc_clear_sky_solar_radiation,
    timeZone, localStdTime, dayOfYear, latitude, longitude,
    tau_b, tau_d);

  // Apply the clear sky values to the given surface
  let surfaceValues = ctx.call(apply_to_surface,
    clearSkyValues, surfaceDir, surfaceTiltAngleDegs, groundReflectance);

  let res = new SolarRadiationValues({
    solar_altitude_rads: clearSkyValues.solar_altitude_rads,

    E_t: surfaceValues.E_t,
    E_t_b: surfaceValues.E_t_b,
    E_t_d: surfaceValues.E_t_d,
    E_t_r: surfaceValues.E_t_r,
    surface_solar_incidence_degs: surfaceValues.surface_solar_incidence_degs,
    surface_azimuth_diff_rads: surfaceValues.surface_azimuth_diff_rads,

    debug: {
      ...clearSkyValues,
      ...surfaceValues.debug,
    }
  });
  return res;
}, {
  autoCache: true
});
